import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, ShieldCheck, KeyRound } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const SIDE_IMG =
  "https://images.pexels.com/photos/35059564/pexels-photo-35059564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("phone"); // phone | otp | done
  const [devOtp, setDevOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("jow_token");
    if (token) {
      api
        .get("/auth/me")
        .then((r) => {
          setMe(r.data);
          setStage("done");
        })
        .catch(() => localStorage.removeItem("jow_token"));
    }
  }, []);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!phone || phone.replace(/\D/g, "").length < 8) {
      toast.error("Please enter a valid phone (with country code).");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      setStage("otp");
      if (data.dev_otp) {
        setDevOtp(data.dev_otp);
        toast.success(`OTP sent (dev mode): ${data.dev_otp}`);
      } else {
        toast.success("OTP sent to your phone.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send OTP.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp });
      localStorage.setItem("jow_token", data.access_token);
      setMe(data.user);
      setStage("done");
      toast.success("Verified. You're in.");
      // Route drivers to their panel; admins to /admin; for customers merge guest cart
      if (data.user?.role === "driver") {
        window.location.href = "/driver/dashboard";
        return;
      }
      if (data.user?.role === "super_admin" || data.user?.role === "manager") {
        window.location.href = "/admin";
        return;
      }
      try {
        await api.post("/cart/merge");
      } catch (e) {
        /* non-fatal */
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Verification failed.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    localStorage.removeItem("jow_token");
    setMe(null);
    setOtp("");
    setPhone("");
    setDevOtp(null);
    setStage("phone");
  };

  return (
    <div data-testid="login-page" className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
      {/* form side */}
      <div className="flex items-center px-6 sm:px-10 lg:px-20 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <span className="label-overline text-jow-gold">Phase 0 · OTP login</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy">
            Step inside the atelier.
          </h1>
          <p className="mt-3 text-jow-navy/60">
            We'll send you a one-time passcode. No passwords, no fuss.
          </p>

          {stage === "phone" && (
            <form onSubmit={requestOtp} className="mt-10 space-y-8" data-testid="otp-request-form">
              <div>
                <label className="label-overline" htmlFor="phone">
                  Mobile number
                </label>
                <input
                  id="phone"
                  type="tel"
                  className="input-jow mt-2"
                  placeholder="+91 9XXXX XXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="phone-input"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary-jow w-full"
                data-testid="send-otp-button"
              >
                {loading ? "Sending..." : "Send OTP"}
                <ChevronRight size={16} />
              </button>
              <p className="text-xs text-jow-navy/50 leading-relaxed">
                By continuing you agree to our test-mode terms. Dev mode shows the
                OTP directly in the response — production routes via MSG91.
              </p>
            </form>
          )}

          {stage === "otp" && (
            <form onSubmit={verifyOtp} className="mt-10 space-y-8" data-testid="otp-verify-form">
              <div className="bg-jow-cream border border-jow-gold/30 px-5 py-4 flex items-start gap-3">
                <KeyRound size={18} className="text-jow-gold mt-0.5" />
                <div className="text-sm">
                  <p className="text-jow-navy">
                    OTP sent to <strong>{phone}</strong>
                  </p>
                  {devOtp && (
                    <p className="text-jow-navy/70 mt-1" data-testid="dev-otp-display">
                      Dev OTP: <code className="font-mono text-jow-pink">{devOtp}</code>
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="label-overline" htmlFor="otp">
                  6-digit OTP
                </label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="input-jow mt-2 tracking-[0.6em] text-2xl font-display"
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  data-testid="otp-input"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStage("phone")}
                  className="btn-secondary-jow flex-1"
                  data-testid="otp-back-button"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary-jow flex-1"
                  data-testid="verify-otp-button"
                >
                  {loading ? "Verifying..." : "Verify"}
                </button>
              </div>
            </form>
          )}

          {stage === "done" && me && (
            <div className="mt-10 space-y-6" data-testid="login-success">
              <div className="border border-jow-gold/40 bg-white p-6 flex items-start gap-3">
                <ShieldCheck size={20} className="text-jow-gold mt-0.5" />
                <div>
                  <p className="font-display text-xl text-jow-navy">
                    Logged in as {me.phone}
                  </p>
                  <p className="text-sm text-jow-navy/60 mt-1" data-testid="login-user-id">
                    User ID: {me.id}
                  </p>
                </div>
              </div>
              <button
                onClick={reset}
                className="btn-secondary-jow"
                data-testid="logout-button"
              >
                Sign out
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {/* image side */}
      <div className="hidden lg:block relative overflow-hidden border-l border-jow-gold/30">
        <img
          src={SIDE_IMG}
          alt="Indian bridal jewellery on red saree"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-jow-navy/30" />
        <div className="absolute bottom-12 left-12 right-12 text-jow-cream">
          <span className="label-overline text-jow-gold">House of Karigars</span>
          <p className="font-display text-3xl mt-3 leading-tight">
            "Jewellery is the most transformative thing you can wear."
          </p>
        </div>
      </div>
    </div>
  );
}
